import apiClient from '@/api/client'
import type { CalendarEvent, MeetingMinutes, PaginatedResponse } from '@/types'

export interface CalendarQueryParams {
  startDate?: string
  endDate?: string
  status?: string
  entityType?: string
  entityId?: string
  organizerId?: string
  page?: number
  size?: number
  /** Narrow the list to one workspace. Omitted means All Workspaces. Note that
   *  legacy events carrying no workspace appear ONLY under All Workspaces —
   *  selecting a workspace hides them, which is the backend's stated rule. */
  workspaceId?: string
}

export interface CreateEventData {
  /** Required. A calendar event has no CRM parent, so there is nothing to
   *  derive a workspace from and the caller must say where it belongs. */
  workspaceId?: string
  title: string
  description?: string
  startTime: string
  endTime: string
  allDay?: boolean
  location?: string
  color?: string
  entityType?: string
  entityId?: string
  meetingLink?: string
  reminderMinutes?: number
  attendeeEmails?: string[]
}

export interface UpdateEventData extends Partial<CreateEventData> {
  status?: string
}

export const calendarApi = {
  getEvents: (params?: CalendarQueryParams) =>
    apiClient.get<PaginatedResponse<CalendarEvent>>('/calendar/events', { params }),

  getEvent: (id: string) =>
    apiClient.get<CalendarEvent>(`/calendar/events/${id}`),

  // AI meeting-prep brief (#1)
  getBrief: (id: string) =>
    apiClient.get(`/calendar/events/${id}/brief`),
  generateBrief: (id: string) =>
    apiClient.post(`/calendar/events/${id}/brief`),

  createEvent: (data: CreateEventData) =>
    apiClient.post<CalendarEvent>('/calendar/events', data),

  updateEvent: (id: string, data: UpdateEventData) =>
    apiClient.put<CalendarEvent>(`/calendar/events/${id}`, data),

  deleteEvent: (id: string) =>
    apiClient.delete(`/calendar/events/${id}`),

  // Backend DELETE /events/{id} sets status=cancelled (soft delete)
  cancelEvent: (id: string) =>
    apiClient.delete(`/calendar/events/${id}`),

  updateAttendeeStatus: (eventId: string, status: string) =>
    apiClient.patch(`/calendar/events/${eventId}/attendance`, { status }),

  saveMeetingMinutes: (eventId: string, data: { content: string; actionItems: string[] }) =>
    apiClient.post<MeetingMinutes>(`/calendar/events/${eventId}/minutes`, {
      notes: data.content,
      action_items: data.actionItems,
    }),
}
